import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { RichText, Link } from '@sitecore-jss/sitecore-jss-react';
import {
  ImgDiv,
  HeroHeader,
  HomeCopyStyle,
  ButtonRow,
  CopyTextHeader,
  CopyTextContent,
  OrangeArrowRight,
  ImageLink,
  HeroLink,
  Header,
  RightButton,
  LinksContainer,
  HorizontalSectionContainer,
  TextSideContainer,
  LeftButton,
  MultipanelImgDiv,
  SinglePanelImgDiv,
  RowImages,
  PaddinglessColumn,
  MultiPanelTextContainer, HeroControlsContainer, DownArrowContainer, DownArrow, Video, HeroControlsSubContainer,
} from './css';
import { styleTypes } from '../ReactComponent/PrimaryButton';
import TOGGLE_SCROLL_TO_REF from './actions';
import { scrollToRef as scrollToRefUtilsFn } from '../../utils/func';

/**
 * A simple Content Block component, with a heading and rich text block.
 * This is the most basic building block of a content site, and the most basic
 * JSS component that's useful.
 */
const TextSide = ({ fields, centerText }) => (
  <TextSideContainer>
    <CopyTextHeader field={fields.heading} centerText={centerText} />
    <CopyTextContent field={fields.content} centerText={centerText} />
    <ButtonRow>

      {fields.leftButton && fields.leftButton.value && fields.leftButton.value.text && (
      <LeftButton
        type="button"
        styleType={styleTypes.SECONDARY}
        onClick={() => {
          window.location.href = fields.leftButton.value.href;
          return window.location.href;
        }}
      >
        {fields.leftButton.value.text}
      </LeftButton>
      )}

      {fields.rightButton && fields.rightButton.value && fields.rightButton.value.text && (
      <RightButton
        type="button"
        onClick={() => {
          window.location.href = fields.rightButton.value.href;
          return window.location.href;
        }}
        iconRightArrow
      >
        {fields.rightButton.value.text}
      </RightButton>
      )}

    </ButtonRow>
  </TextSideContainer>
);

const ImageSide = ({
  fields: {
    image1, image2, image3, link1, link2, link3,
  },
}) => {
  if (
    (image1 && image1.value && Object.keys(image1.value).length)
    && (image2 && image2.value && Object.keys(image2.value).length)
    && (image3 && image3.value && Object.keys(image3.value).length)
  ) {
    return (
      <>
        {image1 && image1.value && image1.value.src && (
        <MultipanelImgDiv className="text-at-bottom first" $img={image1.value.src} alt={image1.value.alt}>
          <ImageLink>
            <Link field={link1} />
          </ImageLink>
        </MultipanelImgDiv>
        )}
        {image2 && image2.value && image2.value.src && (
        <MultipanelImgDiv className="text-at-bottom" $img={image2.value.src} alt={image2.value.alt}>
          <ImageLink>
            <Link field={link2} />
          </ImageLink>
        </MultipanelImgDiv>
        )}
        {image3 && image3.value && image3.value.src && (
        <MultipanelImgDiv className="text-at-bottom" $img={image3.value.src} alt={image3.value.alt}>
          <ImageLink>
            <Link field={link3} />
          </ImageLink>
        </MultipanelImgDiv>
        )}
      </>
    );
  }

  return (
    <div className="col">
      <div className="row">
        {image1 && image1.value && image1.value.src && (
          <SinglePanelImgDiv className="text-at-bottom col first" $img={image1.value.src} alt={image1.value.alt}>
            <ImageLink>
              <Link field={link1} />
            </ImageLink>
          </SinglePanelImgDiv>
        )}
      </div>
    </div>
  );
};

const HomePageCopyBlock = ({ fields, ...rest }) => {
  const { isScrollToRef, toggleScrollToRef, isAuthenticated } = rest;
  const myRef = useRef(null);

  const multiPanel = fields.multiPanel.value;
  const horizontal = fields.horizontal.value;
  const imageFirst = fields.imageFirst.value;

  /**
   * Because the "home page" is made up of multiple instances of HomePageCopyBlock, and
   * because we want to be able to trigger a scroll to one of those instances from another,
   * we're making use of the redux store to determine if we should scroll...
   */
  useLayoutEffect(() => {
    if (isScrollToRef // Value in store says we should scroll...
        && myRef.current // myRef has been initialized...
        && (multiPanel && !horizontal && !imageFirst)) { // Current component instance represents the one we want to scroll to...
      scrollToRefUtilsFn(myRef); // Scroll...
      toggleScrollToRef(); // Reset the redux store value so we can scroll again...
    }
  }, [isScrollToRef]);

  if (multiPanel) {
    return (
      <HomeCopyStyle ref={myRef}>
        {horizontal
          ? (
            <HorizontalSectionContainer $imageFirst={imageFirst}>
              <PaddinglessColumn $desktop={6}>
                <TextSide fields={fields} />
              </PaddinglessColumn>
              <PaddinglessColumn $desktop={6}>
                {fields.multiPanel && <ImageSide fields={fields} />}
              </PaddinglessColumn>
            </HorizontalSectionContainer>
          )
          : (
            imageFirst ? (
              <div className="col-md-12">
                <div className="row">
                  {fields.multiPanel && <ImageSide fields={fields} />}
                </div>
                <div className="row">
                  <TextSide fields={fields} />
                </div>
              </div>
            ) : (
              <>
                {fields.heading && (
                <MultiPanelTextContainer>
                  <TextSide fields={fields} centerText />
                </MultiPanelTextContainer>
                )}
                <RowImages>
                  {fields.multiPanel && <ImageSide fields={fields} />}
                </RowImages>
              </>
            )
          )}
      </HomeCopyStyle>
    );
  }

  return (
    <div>
      <ImgDiv className="hero-image">
        <Video
          playsinline
          autoPlay
          muted
          loop
          poster={fields.videoPoster && fields.videoPoster.value.src}
        >
          <source
            src={fields.video && fields.video.value.src}
            type="video/mp4"
          />
        </Video>
        <HeroControlsContainer>
          <HeroControlsSubContainer>
            <HeroHeader>
              <Header field={fields.heading} />
            </HeroHeader>
            <div className="row"><RichText className="contentDescription col" field={fields.content} /></div>
            <ButtonRow className="row">

              {fields.leftButton && (
              <LeftButton
                type="button"
                onClick={() => {
                  window.location.href = fields.leftButton.value.href;
                  return window.location.href;
                }}
                iconRightArrow
              >
                {fields.leftButton.value.text}
              </LeftButton>
              )}

              {!isAuthenticated
                   && fields.rightButton
                    && (
                      <RightButton
                        type="button"
                        styleType={styleTypes.SECONDARY}
                        onClick={() => {
                          window.location.href = fields.rightButton.value.href;
                          return window.location.href;
                        }}
                        iconRightArrow
                      >
                        {fields.rightButton.value.text}
                      </RightButton>
                    )}

            </ButtonRow>

            <LinksContainer className="col">
              {fields.link1 && fields.link1.value && fields.link1.value.text && (
              <HeroLink className="row">
                <Link field={fields.link1} />
                <OrangeArrowRight />
              </HeroLink>
              )}
              {fields.link2 && fields.link2.value && fields.link2.value.text && (
              <HeroLink className="row">
                <Link field={fields.link2} />
                <OrangeArrowRight />
              </HeroLink>
              )}
              {fields.link3 && fields.link3.value && fields.link3.value.text && (
              <HeroLink className="row">
                <Link field={fields.link3} />
                <OrangeArrowRight />
              </HeroLink>
              )}
            </LinksContainer>
          </HeroControlsSubContainer>
        </HeroControlsContainer>

        <DownArrowContainer>
          <DownArrow onClick={() => toggleScrollToRef()} />
        </DownArrowContainer>
      </ImgDiv>
    </div>
  );
};

const mapStateToProps = (state) => ({
  isScrollToRef: state.homePage.toggleScrollToRef,
  isAuthenticated: state.profile.isAuthenticated,
});

const mapDispatchToProps = (dispatch) => ({
  toggleScrollToRef: () => dispatch({ type: TOGGLE_SCROLL_TO_REF }),
});

export default connect(mapStateToProps, mapDispatchToProps)(HomePageCopyBlock);

HomePageCopyBlock.propTypes = {
  fields: PropTypes.shape({
    heading: PropTypes.object,
    content: PropTypes.object,
    leftButton: PropTypes.shape({
      value: PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    rightButton: PropTypes.shape({
      value: PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    multiPanel: PropTypes.object,
    horizontal: PropTypes.object,
    imageFirst: PropTypes.object,
    image1: PropTypes.object,
    image2: PropTypes.object,
    image3: PropTypes.object,
    link1: PropTypes.object,
    link2: PropTypes.object,
    link3: PropTypes.object,
    video: PropTypes.object,
    videoPoster: PropTypes.object,
  }),
  isScrollToRef: PropTypes.bool,
  toggleScrollToRef: PropTypes.func,
  isAuthenticated: PropTypes.bool,
};

TextSide.propTypes = {
  fields: PropTypes.shape({
    heading: PropTypes.object,
    content: PropTypes.object,
    leftButton: PropTypes.shape({
      value: PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    rightButton: PropTypes.shape({
      value: PropTypes.shape({
        text: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
  }),
  centerText: PropTypes.bool,
};

ImageSide.propTypes = {
  fields: PropTypes.shape({
    image1: PropTypes.object,
    image2: PropTypes.object,
    image3: PropTypes.object,
    link1: PropTypes.object,
    link2: PropTypes.object,
    link3: PropTypes.object,
  }),
};

